/* eslint-disable no-param-reassign */

'use client';

import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type { Key } from 'react-aria';
import type { LayoutInfo } from '../layouts/types';
import { difference } from '../layouts/utils';
import type { ListFilterState } from '../List/types';

type Props = {
  layoutInfo: Map<Key, LayoutInfo>;
  sectionGap: number;
  scrollCache: RefObject<{ x: number; y: number } | null>;
};

/**
 * Stabilize the scroll position when (mount or unmount) mutations occur within
 * the collection. This is necessary to prevent the scroll position from jumping
 * when items are added or removed from the collection above the visible rect.
 *
 * This stabilization works in conjunection with `updateItemSize` in `useVirtualizedList`
 * which handles the stabilization of the scroll position when items are resized.
 */

export function useStabilizeMutation<T>(
  { layoutInfo, sectionGap, scrollCache }: Props,
  state: ListFilterState<T>,
  ref: RefObject<HTMLElement | null>
) {
  const previousLayoutInfo = useRef(layoutInfo);
  const previousCollection = useRef(state.collection);

  useEffect(() => {
    if (previousCollection.current !== state.collection) {
      const { toAdd, toRemove } = difference(
        previousLayoutInfo.current,
        layoutInfo
      );

      let offsetY = 0;

      toAdd.forEach((key) => {
        const info = layoutInfo.get(key);
        if (
          info &&
          scrollCache.current &&
          info.rect.y + info.rect.height < scrollCache.current.y
        ) {
          if (info.type === 'item') {
            // gap end can be undefined
            const gapEnd = info.gapEnd ?? 0;
            offsetY += info.rect.height + gapEnd;
          } else if (info.type === 'header') {
            offsetY += info.rect.height;
          } else if (info.type === 'section') {
            const gapEnd = info.gapEnd ?? sectionGap;
            offsetY += gapEnd;
          }
        }
      });

      toRemove.forEach((key) => {
        const info = previousLayoutInfo.current.get(key);
        if (
          info &&
          scrollCache.current &&
          info.rect.y + info.rect.height < scrollCache.current.y
        ) {
          if (info.type === 'item') {
            // gap end can be undefined
            const gapEnd = info.gapEnd ?? 0;
            offsetY -= info.rect.height + gapEnd;
          } else if (info.type === 'section') {
            const gapEnd = info.gapEnd ?? sectionGap;
            offsetY -= gapEnd;
          }
        }
      });

      if (offsetY && scrollCache.current) {
        const nextTop = Math.max(0, scrollCache.current.y + offsetY);
        if (ref.current) {
          ref.current.scrollTop = nextTop;
        }
        scrollCache.current.y = nextTop;
      }
    }

    previousLayoutInfo.current = layoutInfo;
    previousCollection.current = state.collection;
  });
}
